<template>
  <div class="invention-documents">
    <div class="toolbar">
      <div class="search-bar">
        <MdIcon name="magnify" size="sm" class="search-icon" />
        <input v-model="search" type="text" placeholder="Search" class="search-input" />
      </div>
      <div class="filters">
        <span class="filter-group">
          <span class="filter-label">Document Type:</span>
          <Dropdown :value="typeFilter" :options="typeFilterOptions" @input="typeFilter = $event" />
        </span>
      </div>
    </div>

    <div v-if="isLoadingFiles" class="loading">
      <MdIcon name="loading" size="lg" spin />
    </div>
    <div v-else-if="!hasResults" class="empty">No documents found.</div>
    <div v-else class="list">
      <template v-for="(docs, tag) in filteredGroupedFiles">
        <div :key="'h-' + tag" class="group-header">{{ tag }}</div>
        <div
          v-for="doc in docs"
          :key="doc.id"
          class="doc-row"
          :class="{ 'is-attaching': processingIds.includes(doc.id), 'is-attached': attachedIds.includes(doc.id) }"
        >
          <div class="doc-info">
            <span class="doc-name">{{ doc.name }}</span>
            <span class="doc-sub">
              <span v-if="doc.description" class="doc-tag">{{ doc.description }}</span>
            </span>
            <span class="doc-extensions">
              <span v-for="(group, ext) in groupedDocFiles(doc.files)" :key="ext" class="ext-badge" :title="fileGroupTitle(ext, group)">
                <MdIcon :name="extensionIcon(ext)" size="sm" />
                <span
                  >{{ ext.toUpperCase() }}<template v-if="group.length > 1"> ×{{ group.length }}</template></span
                >
              </span>
            </span>
          </div>
          <Button
            color="secondary"
            class="use-button"
            :title="attachedIds.includes(doc.id) ? 'Already attached' : 'Use in chat'"
            :disabled="processingIds.includes(doc.id) || attachedIds.includes(doc.id)"
            @click="useInChat(doc)"
          >
            <MdIcon
              :name="processingIds.includes(doc.id) ? 'progress-clock' : attachedIds.includes(doc.id) ? 'check' : 'chat-plus-outline'"
              size="sm"
            />
          </Button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MdIcon from '@/components/common/MdIcon';
import Button from '@/components/common/Button';
import Dropdown from '@/components/common/Dropdown';

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp']);

export default {
  components: { MdIcon, Button, Dropdown },
  props: {
    processingIds: { type: Array, default: () => [] },
    attachedIds: { type: Array, default: () => [] }
  },
  emits: ['use-doc'],
  data() {
    return { search: '', typeFilter: '' };
  },
  computed: {
    ...mapState({
      files: s => s.inventionFiles.files,
      isLoadingFiles: s => s.inventionFiles.isLoadingFiles,
      sessionContext: s => s.prompts.sample.sessionContext,
      organizations: s => s.organizations.collection
    }),
    typeFilterOptions() {
      return [{ id: '', label: 'All' }, ...this.allTags.map(t => ({ id: t, label: t }))];
    },
    allTags() {
      const tags = new Set();
      this.files
        .filter(d => !d.isDeleted)
        .forEach(doc => {
          (doc.tags && doc.tags.length ? doc.tags : ['other']).forEach(t => tags.add(t));
        });
      return [...tags].sort();
    },
    filteredGroupedFiles() {
      const q = this.search.trim().toLowerCase();
      const t = this.typeFilter;
      const activeDocs = this.files.filter(doc => {
        if (doc.isDeleted) return false;
        if (t) {
          const docTags = doc.tags && doc.tags.length ? doc.tags : ['other'];
          if (!docTags.includes(t)) return false;
        }
        if (q && !doc.name.toLowerCase().includes(q)) return false;
        return true;
      });
      // Sort newest first
      activeDocs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      // Group by date label
      return activeDocs.reduce((acc, doc) => {
        const label = doc.createdAt
          ? new Date(doc.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : 'Unknown date';
        if (!acc[label]) acc[label] = [];
        acc[label].push(doc);
        return acc;
      }, {});
    },
    hasResults() {
      return Object.keys(this.filteredGroupedFiles).length > 0;
    },
    clientOrganizationId() {
      const invention = this.sessionContext?.invention;
      if (!invention) return null;
      const ref = invention.references?.find(r => r.startsWith('HARRITY:'));
      if (!ref) return null;
      const alias = ref
        .split(':')
        .pop()
        .split('-')[0];
      return this.organizations?.find(o => o.aliases.includes(alias))?.id ?? null;
    }
  },
  methods: {
    groupedDocFiles(files) {
      return (files || []).reduce((acc, f) => {
        const ext = (f.extension || 'other').toLowerCase();
        if (!acc[ext]) acc[ext] = [];
        acc[ext].push(f);
        return acc;
      }, {});
    },
    fileGroupTitle(ext, group) {
      const names = group.map(f => f.name).join(', ');
      return group.length > 1 ? `${ext.toUpperCase()} ×${group.length}: ${names}` : names;
    },
    extensionIcon(ext) {
      const e = (ext || '').toLowerCase();
      if (e === 'pdf') return 'file-pdf-box';
      if (e === 'docx' || e === 'doc') return 'file-word';
      if (e === 'pptx' || e === 'ppt' || e === 'pptm') return 'file-powerpoint';
      if (IMAGE_EXTS.has(e)) return 'file-image-outline';
      if (e === 'txt') return 'file-document-outline';
      if (e === 'eml') return 'email-outline';
      if (e === 'md') return 'language-markdown';
      return 'file-outline';
    },
    useInChat(doc) {
      if (!doc.files || !doc.files.length) return;
      this.$emit('use-doc', doc);
    }
  }
};
</script>

<style lang="scss" scoped>
.invention-documents {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  // ── Toolbar ─────────────────────────────────────────────────────────
  .toolbar {
    flex-shrink: 0;
    padding: 10px 12px 8px;
    border-bottom: 1px solid var(--color-border);

    .search-bar {
      display: flex;
      align-items: center;
      gap: 6px;
      border: 2px solid rgba(128, 128, 128, 0.5);
      border-radius: 8px;
      padding: 8px 12px;
      margin-bottom: 8px;
      background: rgba(128, 128, 128, 0.1);
      transition: border-color 0.15s ease, background 0.15s ease;

      &:focus-within {
        border-color: rgba(128, 128, 128, 0.8);
        background: rgba(128, 128, 128, 0.15);
      }

      .search-icon {
        opacity: 0.45;
        flex-shrink: 0;
      }

      .search-input {
        flex: 1;
        border: none;
        outline: none;
        background: transparent;
        font-size: 15px;
        color: inherit;

        &::placeholder {
          color: var(--color-text-secondary);
          opacity: 0.7;
        }
      }
    }

    .filters {
      display: flex;
      align-items: center;
      gap: 16px;
      font-size: 15px;

      .filter-group {
        display: flex;
        align-items: center;
        gap: 6px;

        .filter-label {
          color: var(--color-text-secondary);
          white-space: nowrap;
        }

        ::v-deep .dropdown {
          .dropdown-label {
            font-size: 15px;
          }

          ul li {
            font-size: 15px;
          }
        }
      }
    }
  }

  // ── States ──────────────────────────────────────────────────────────
  .loading,
  .empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
    font-size: 15px;
  }

  // ── Scrollable list ─────────────────────────────────────────────────
  .list {
    flex: 1;
    overflow-y: auto;

    .group-header {
      padding: 6px 12px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--color-text-secondary);
      background: transparent;
    }

    .doc-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 12px;
      margin: 0 8px 6px;
      background: var(--color-bg-secondary, rgba(255, 255, 255, 0.05));
      border: 1px solid var(--color-border);
      border-radius: 8px;
      transition: background 0.12s ease, border-color 0.12s ease;

      &:hover {
        background: var(--color-bg-elevated, rgba(255, 255, 255, 0.09));
        border-color: rgba(255, 255, 255, 0.18);
      }

      .doc-info {
        display: flex;
        flex-direction: column;
        gap: 5px;
        overflow: hidden;
        min-width: 0;

        .doc-name {
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .doc-sub {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .doc-tag {
          font-size: 12px;
          font-style: italic;
          color: var(--color-text-secondary);
        }

        .doc-extensions {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .ext-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-secondary);
          background: var(--color-bg-elevated, rgba(128, 128, 128, 0.12));
          border: 1px solid var(--color-border);
          border-radius: 4px;
          padding: 2px 5px;
          cursor: default;
        }
      }

      .use-button {
        flex-shrink: 0;
        opacity: 0;
        transition: opacity 0.12s ease;
      }

      &:hover .use-button,
      &.is-attaching .use-button,
      &.is-attached .use-button {
        opacity: 1;
      }
    }
  }
}
</style>
